import React from 'react';
import {css} from 'glamor';
import {useQuery} from "@apollo/client";

import * as theme from '../../../theme';
import {withInstallation} from '../../../components/InstallationContext';
import {FlashMessage} from '../../../components/FlashMessage';
import {Button} from '../../../components/Button';
import {Container, CardContainer} from '../../../components/Container';
import {Left, Right} from '../../../components/Header';
import {Well} from '../../../components/Well';

import {Byline, H1, H2} from '../../../components/Typography';
import {Table, THead, TBody} from '../../../components/Table';
import {Loading, LoadingError} from '../../../components/LoadingFork';
import {ListSanctionedPlateTime} from './queries';
import authManager, {PERMISSIONS} from "../../../lib/authManager";
import {PermissionOnly} from "../../../components/AdminOnly";

function Header(props) {
    return <div
        className={css({
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'stretch',
        })}
        {...props}
    />
}

const rowStyle = css({
    '> td': {
        verticalAlign: 'middle',
        height: 60,
        borderBottom: `1px solid ${theme.gray10}`,
        transition: 'all .2s linear',
    },
    '> td:first-child': {paddingLeft: 10},
    '> td:last-child': {paddingRight: 10},
    ':hover > td': {
        background: theme.gray10,
        cursor: 'pointer',
    },
});

export function getStatus(status) {
    switch (status) {
        case 'current':
            return '✅';
        case 'pending':
            return '🔁';
        case 'archived':
            return '📦';
        default:
            return '❓';
    }
}

function formatTimestamp(timestamp) {
    const date = new Date(timestamp); // keeps system time
    const pad = (n) => String(n).padStart(2, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1); // months are 0-indexed
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    return `${year}-${month}-${day} ${hours}:${minutes}`;
}

function SanctionedTimeRow({ sanctionedTime, onClick }) {
    const { start_date, end_date, status, id } = sanctionedTime;

    return (
      <tr key={id} className={rowStyle} onClick={onClick}>
          <td>{start_date ? formatTimestamp(start_date) : '-'}</td>
          <td>{end_date ? formatTimestamp(end_date) : '-'}</td>
          <td>{getStatus(status)}</td>
      </tr>
    );
}

function SanctionedTimesOnly({match, installation, location, history}) {
    const {data = {}, loading, error} = useQuery(ListSanctionedPlateTime, {
        fetchPolicy: 'no-cache',
        variables: {
            installationId: installation.id
        }
    });

    const sanctionedTime = (data?.getSanctionedByFacility || []).slice().sort((a, b) => {
        if (a.status === 'archived' && b.status !== 'archived') return 1;
        if (a.status !== 'archived' && b.status === 'archived') return -1;
        return 0; // preserve original order between non-archived
    });

    return <div>
        <CardContainer>
            <Container size="large" spaceAbove="small" spaceBelow="small">
                <Header>
                    <Left>
                        <H1>Sanctioned Only Entry Times</H1>
                    </Left>
                    <Right>
                        {!loading && sanctionedTime.length !== 0 && (
                          <PermissionOnly permissions={PERMISSIONS.SANCTIONED_TIMES_MANAGE}>
                            <Button to={`${match.url}/new`} style={{'background-color': '#3127B8'}}>
                                Add new time
                            </Button>
                          </PermissionOnly>
                        )}
                    </Right>
                </Header>
                <Byline>
                    During these times, only sanctioned plates (bookings and whitelist) will be allowed entry.
                </Byline>
            </Container>
            <Container spaceBelow="large">
                <FlashMessage locationState={location.state}/>
                {loading ? <Loading/> : null}
                {error ? <LoadingError error={error}/> : null}
                {!loading && sanctionedTime.length === 0 && <Well>
                    <div>
                        <H2 spaceBelow="large">No data available</H2>
                        <PermissionOnly permissions={PERMISSIONS.SANCTIONED_TIMES_MANAGE}>
                            <Button spaceBelow="none" to={`${match.url}/new`}>
                                Create a new time now
                            </Button>
                        </PermissionOnly>
                    </div>
                </Well>}
                {!loading && <Table>
                    <THead>
                        <tr>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Status</th>
                        </tr>
                    </THead>
                    <TBody>{sanctionedTime.map((time) => {
                        return <SanctionedTimeRow key={time.id} timezone={installation.timeZoneName} sanctionedTime={time} onClick={() => {
                            if (!authManager.hasPermission(PERMISSIONS.SANCTIONED_TIMES_MANAGE, installation.id)) {
                                return;
                            }

                            history.push(`${match.url}/${time.id}`);
                        }}/>
                    })}</TBody>
                </Table>}
            </Container>
        </CardContainer>
    </div>
}

export default withInstallation(SanctionedTimesOnly)
